import {
  Component,
  computed,
  inject,
  input,
  Input,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  Inject,
  PLATFORM_ID,
  signal,
} from "@angular/core";
import { Subscription as RxSubscription } from "rxjs";
import * as Highcharts from "highcharts";
import { ReadablePacketSizePipe, ReadableSizePipe } from "../../../helpers/bitspipe/bitspipe";
import { ExtendedChartComponent } from "../../models/base/extendedchartcomponent";
import { ApiHelper } from "../../../helpers/apihelper";
import { SurfLog } from "../../../helpers/surflog";
import { MessageService } from "../../../services/messageservice";
import { ChartFilterType } from "../../../helpers/types";
import { MatDialog } from "@angular/material/dialog";
import { DialogSls } from "../../dialogs/dialog-sls/dialog-sls";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { WirelessSubscriptionDetails } from "../../../pages/subscription-detail/types";
import { ChartChoice, PortFilterItem } from "./support";
import { AngularSvgIconModule } from "angular-svg-icon";
import { CommonModule, isPlatformBrowser } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { HighchartsChartModule } from "highcharts-angular";
import { FlatpickrModule } from "angularx-flatpickr";
import { extendHighcharts } from "../../../helpers/highcharts";
import { NgClickOutsideDirective } from "ng-click-outside2";

@Component({
  selector: "linechart",
  templateUrl: "linechart.html",
  imports: [
    AngularSvgIconModule,
    CommonModule,
    FlatpickrModule,
    FormsModule,
    HighchartsChartModule,
    NgClickOutsideDirective,
    ReadableSizePipe,
    TranslateModule,
  ],
})
export class LinechartComponent extends ExtendedChartComponent implements OnChanges, AfterViewInit {
  private translate = inject(TranslateService);
  private api = inject(ApiHelper);
  private msgsrv = inject(MessageService);
  private dialog = inject(MatDialog);

  protected subscription = input.required<any>();

  @Input() splineData: any;
  @Input() happyState = false;
  @Input() chartChoices: ChartChoice[] = [];

  protected circuits = computed(() => this.subscription().circuits);
  protected domain = computed(() => this.subscription().domain);
  protected product = computed(() => this.subscription().product);
  protected saps = computed(() => this.subscription().saps);
  protected services = computed(() => this.subscription().services);
  protected subscriptionId = computed(() => this.subscription().subscriptionId);

  protected isIp = computed(() => this.product().type === "IP");
  protected isLightPath = computed(() => this.product().type === "LightPath");
  protected isRedundantLightPath = computed(() => this.subscription().tags.indexOf("LR") !== -1);
  protected isPort = computed(() => this.product().type === "Port");
  protected isWireless = computed(() => this.product().type == "Wireless");
  protected isL2Vpn = computed(() => this.product().type === "L2VPN");
  protected isL3Vpn = computed(() => this.product().type === "L3VPN");
  protected isFw = computed(() => this.product().type === "FW");

  highcharts: typeof Highcharts = Highcharts;
  chartConstructor = "chart"; // optional string, defaults to 'chart'
  chartOptions: Highcharts.Options = this.chartInfo; // required
  updateFlag = false; // optional boolean
  oneToOneFlag = true; // optional boolean, defaults to false
  packetsSubFilters = ["unicast", "broadcast", "multicast"];
  packetsSubFilterActive = "unicast";
  splineDataRaw: any;
  serviceInstanceId = "";
  instanceIdFilter = "";
  vlans = null;
  protected selectedPreset = "1w";
  protected presets = ["1d", "1w", "1m", "1y"];

  public portsFilterData: PortFilterItem[] = [];
  public latestChartData = { in: 0, out: 0 };
  public chartFilter = 2;
  public exportTitle = "";
  public defaultTimeWindow = 7 * 24 * 3600 * 1000;
  public firstDate = new Date(Date.now() + -this.defaultTimeWindow);
  public secondDate = new Date();
  public circuitFilter: any;
  public portFilter: any;
  public kpiFilter: any;
  public defaultDate = true;
  public tomorrow = new Date(new Date().setDate(new Date().getDate() + 1));
  public exportOverflowVisible = false;
  public selectedPortSubscription: any;
  private chartNotification = signal("Retrieving data...");
  private chartDataModus = signal("bits");
  protected isBrowser = signal(false);

  protected showBits = computed(() => this.chartDataModus() === "bits");
  protected showPackets = computed(() => this.chartDataModus() === "packets");

  private subChartFilter: RxSubscription;
  private inError = false;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    super();

    if (isPlatformBrowser(platformId)) {
      extendHighcharts(this.highcharts);
      this.isBrowser.set(true);
    }

    this.circuitFilter = "";
    this.portFilter = "";
    this.kpiFilter = "clients";

    this.subChartFilter = this.msgsrv.chartFilter$.subscribe((filter: ChartFilterType) => {
      const circuitFilterData = filter.connection;
      // do this so the port <select> will select the correct port.
      // it needs to be one of the objects in this.circuitFilter.endpoints.
      if (circuitFilterData) {
        if (this.isPort()) {
          this.circuitFilter = this.services().find(
            (el) => filter.vlanranges.includes(el.vlanrange) && el.subscriptionId === circuitFilterData.subscriptionId,
          );

          this.selectedPortSubscription = this.circuitFilter.subscriptionId;

          this.updateInstanceIdFilters(this.circuitFilter.subscriptionId, this.circuitFilter.vlanrange);
        }

        if (this.isIp() || this.isFw()) {
          if (this.subscription()?._saps !== undefined) {
            this.portFilter = this.subscription()?._saps.find(
              (el) =>
                filter.vlanranges.includes(el.vlanrange)
                && el.port?.subscriptionId === circuitFilterData.subscriptionId,
            );
          }

          let portFilterItem = this.portsFilterData.find(
            (el) =>
              el.value.port?.subscriptionId === circuitFilterData.subscriptionId
              || el.value.firewallSubscriptionId === circuitFilterData.subscriptionId
              || el.value.otherSubscriptionId === circuitFilterData.subscriptionId
              || el.value.subscriptionInstanceId === circuitFilterData.ipGateway?.subscriptionInstanceId,
          );
          if (portFilterItem !== undefined) {
            this.portFilter = portFilterItem.value;
          }

          this.updateServiceInstanceIdFilters();
        }

        if (this.isL2Vpn() || this.isL3Vpn()) {
          let portFilterItem = this.portsFilterData.find(
            (el) =>
              el.value.port?.subscriptionId === circuitFilterData.subscriptionId
              || el.value.firewallSubscriptionId === circuitFilterData.subscriptionId,
          );
          if (portFilterItem !== undefined) {
            this.portFilter = portFilterItem.value;
          }

          this.updateServiceInstanceIdFilters();
        }
      }
    });
  }

  get chartInfo(): any {
    const bitsPipe = new ReadableSizePipe();
    const packetPipe = new ReadablePacketSizePipe();
    const thisContext = this;
    return {
      chart: {
        type: "line",
        zoomType: "x",
        height: 400,
        showAxes: false,
        backgroundColor: "#FAFBFD",
        spacingLeft: 24,
        spacingRight: 24,
        spacingTop: 48,
        selectionMarkerFill: "rgba(0,0,0,0.05)",
        resetZoomButton: {
          position: {
            x: 0,
            y: 8,
          },
        },
        style: {
          fontFamily: "Proxima",
        },
        events: {
          selection(event) {
            if (event.xAxis !== undefined) {
              /**
               * trigger new data loading after selecting in the chart
               */
              thisContext.firstDate = new Date(event.xAxis[0].min);
              thisContext.secondDate = new Date(event.xAxis[0].max);
              thisContext.defaultDate = false;
              if (this.isWireless()) {
                thisContext.getNewWirelessData();
              } else {
                thisContext.getNewData();
              }
            }
          },
        },
      },
      lang: {
        noData: "Retrieving data...",
      },

      exporting: {
        chartOptions: {
          chart: {
            style: {
              fontFamily: "Helvetica",
            },
          },
          yAxis: [
            {
              labels: {
                style: {
                  font: "9px Helvetica",
                },
              },
              title: {
                // text: 'Test titel',
                text: thisContext.chartDataModus,
              },
            },
          ],
          xAxis: [
            {
              type: "datetime",
              dateTimeLabelFormats: {
                hour: "%H:%M",
                minute: "%H:%M",
              },
              labels: {
                style: {
                  font: "9px Helvetica",
                },
              },
            },
          ],
        },
        enabled: false,

        csv: {
          decimalPoint: ".",
          lineDelimiter: "\n",
        },
      },

      title: {
        text: null,
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        shared: true,
        shadow: false,
        backgroundColor: "white",
        borderColor: "#E6E9EB",
        pointFormatter() {
          const tipLabel = thisContext.translate.instant(`Chart.TrafficGraph.Tooltip.Wireless.${this.series.name}`);
          if (thisContext.isWireless()) {
            const v = parseFloat(this.y).toFixed(2);
            return `<span style="color:${this.color}">\u25CF</span> ${tipLabel}: <b>${v}</b><br/>`;
          } else {
            const v = thisContext.showBits() ? bitsPipe.transform(this.y) : packetPipe.transform(this.y);
            return `<span style="color:${this.color}">\u25CF</span> ${tipLabel}: <b>${v}</b><br/>`;
          }
        },
      },
      yAxis: {
        title: {
          text: null,
        },

        gridLineColor: "#E6E9EB",
        labels: {
          style: {
            color: "#7f8c8d",
            font: "9px Proxima",
          },
        },
        // minRange: 10000
      },
      xAxis: {
        type: "datetime",
        dateTimeLabelFormats: {
          hour: "%H:%M",
          minute: "%H:%M",
        },
        minPadding: 0,
        maxPadding: 0,
        lineColor: "transparent",
        minorTickLength: 0,
        tickLength: 0,
        labels: {
          style: {
            color: "#7f8c8d",
            font: "9px Proxima",
          },
        },
      },
      credits: {
        enabled: false,
      },
      series: [
        {
          name: "in",
          marker: {
            enabled: false,
            symbol: "circle",
            radius: 2,
          },
          data: [],
          color: "#177abf",
          lineWidth: 1,
          fillColor: {
            linearGradient: { x1: 1, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, "rgba(231, 134, 122, 0.15)"],
              [1, "rgba(255,255,255,.0)"],
            ],
          },
        },
        {
          name: "out",
          marker: {
            enabled: false,
            symbol: "circle",
            radius: 2,
          },
          data: [],
          color: "#e7303a",
          lineWidth: 1,
          fillColor: {
            linearGradient: { x1: 1, y1: 0, x2: 0, y2: 1 },
            stops: [
              [0, "rgba(60, 106, 133, 0.15)"],
              [1, "rgba(255,255,255,.0)"],
            ],
          },
        },
        {
          name: "Piek in",
          data: [],
          color: "#ee7628",
          lineWidth: 1,
          marker: {
            enabled: false,
            symbol: "circle",
            radius: 2,
          },
          fillColor: {
            linearGradient: { x1: 1, y1: 0, x2: 0, y2: 1 },
            stops: [],
          },
        },
        {
          name: "Piek uit",
          data: [],
          color: "#2ca055",
          lineWidth: 1,
          marker: {
            enabled: false,
            symbol: "circle",
            radius: 2,
          },
          fillColor: {
            linearGradient: { x1: 1, y1: 0, x2: 0, y2: 1 },
            stops: [],
          },
        },

        {
          name: "",
          data: [],
          color: "#8B5CF6",
          lineWidth: 1,
          marker: {
            enabled: false,
            symbol: "circle",
            radius: 2,
          },
        },
        {
          name: "",
          data: [],
          color: "#EC4899",
          lineWidth: 1,
          marker: {
            enabled: false,
            symbol: "circle",
            radius: 2,
          },
        },
        {
          name: "",
          data: [],
          color: "CadetBlue",
          lineWidth: 1,
          marker: {
            enabled: false,
            symbol: "circle",
            radius: 2,
          },
        },
        {
          name: "",
          data: [],
          color: "Gold",
          lineWidth: 1,
          marker: {
            enabled: false,
            symbol: "circle",
            radius: 2,
          },
        },
      ],
    };
  }

  /**
   * Switch between bits and packets.
   */
  // Todo: networkdashboard-api #41 (correct typo?)
  toggleBitsPackets() {
    if (this.showBits()) {
      this.chartDataModus.set("packets");
    } else {
      this.chartDataModus.set("bits");
    }

    if (this.isWireless()) {
      this.getNewWirelessData();
    } else {
      this.getNewData();
    }
  }

  /**
   * Switch between packet types (unicast/multicast/broadcast)
   *
   * @param chartDataModusValue
   */
  updatePacketsSubFilters(chartDataModusValue) {
    this.packetsSubFilterActive = chartDataModusValue;
    // replaced `this.updateChartSeries()` with `getNewData()`
    // to re-fetch packet data since it will be
    // requested for this specific type only.
    if (this.isWireless()) {
      this.getNewWirelessData();
    } else {
      this.getNewData();
    }
  }

  /**
   * Chart data from this port
   *
   * @param serviceInstanceIdValue
   */
  updateServiceInstanceIdFilters(serviceInstanceIdValue = null) {
    if (serviceInstanceIdValue) {
      if (serviceInstanceIdValue !== "-1") {
        this.portFilter = serviceInstanceIdValue;
      } else {
        this.portFilter = null;
      }
    }

    if (this.isWireless()) {
      this.getNewWirelessData();
    } else {
      this.getNewData();
    }
  }

  /**
   * Chart data from this subscription
   *
   * @param instanceIdValue
   * @param vlanrange
   */
  updateInstanceIdFilters(instanceIdValue, vlanrange = null) {
    if (instanceIdValue === undefined) {
      instanceIdValue = "";
    }

    this.instanceIdFilter = instanceIdValue;
    this.vlans = vlanrange;

    if (this.isWireless()) {
      this.getNewWirelessData();
    } else {
      this.getNewData();
    }
  }

  setChartInstance(chart: Highcharts.Chart) {
    this.chartRef = chart;
  }

  toggleLegendItem(series?: string) {
    super.toggleLegendItem(series);
  }

  updateExportTitle() {
    if (this.showBits()) {
      this.translate.get("Chart.Label.BitsS").subscribe((exportTitleTranslation: string) => {
        this.exportTitle = exportTitleTranslation;
      });
    }

    if (this.showPackets()) {
      this.translate.get("Chart.Label.PacketsS").subscribe((exportTitleTranslation: string) => {
        this.exportTitle = exportTitleTranslation;
      });
    }

    this.chartRef.update({
      exporting: {
        chartOptions: {
          yAxis: [
            {
              title: {
                text: this.exportTitle,
              },
            },
          ],
        },
      },
    });
  }

  exportAsPNG() {
    this.updateExportTitle();
    this.chartRef.exportChartLocal({ type: "image/png" });
  }
  exportAsJPG() {
    this.updateExportTitle();
    this.chartRef.exportChartLocal({ type: "image/jpeg" });
  }
  exportAsCSV() {
    this.updateExportTitle();
    this.chartRef.downloadCSV();
  }

  setDatePreset(preset: string) {
    this.selectedPreset = preset;
    this.firstDate = new Date();

    switch (preset) {
      case "1d":
        this.firstDate.setDate(this.firstDate.getDate() - 1);
        break;
      case "1w":
        this.firstDate.setDate(this.firstDate.getDate() - 7);
        break;
      case "1m":
        this.firstDate.setMonth(this.firstDate.getMonth() - 1);
        break;
      case "1y":
        this.firstDate.setDate(this.firstDate.getDate() - 365);
        break;
    }

    this.secondDate = new Date();
    if (this.isWireless()) {
      this.getNewWirelessData();
    } else {
      this.getNewData();
    }
  }

  /**
   * Handle newly picked dates (chart range)
   */
  onDateChange() {
    this.selectedPreset = "";
    if (this.secondDate > new Date()) {
      this.secondDate = new Date();
    }

    if (this.firstDate > this.secondDate) {
      this.firstDate = new Date(this.secondDate.getTime() + -this.defaultTimeWindow);
    }

    this.defaultDate = false;
    if (this.isWireless()) {
      this.getNewWirelessData();
    } else {
      this.getNewData();
    }
  }

  resetDatePickers() {
    this.selectedPreset = "";
    this.secondDate = new Date();
    this.firstDate = new Date(this.secondDate.getTime() + -this.defaultTimeWindow);
    this.defaultDate = true;

    if (this.isWireless()) {
      this.getNewWirelessData();
    } else {
      this.getNewData();
    }
  }

  toggleExportOptions() {
    this.exportOverflowVisible = !this.exportOverflowVisible;
  }

  /**
   * Show the SLS (Availability) dialog.
   */
  showSlsDialog() {
    const dialogRef = this.dialog.open(DialogSls, {
      data: this.subscription,
    });
  }

  ngAfterViewInit() {
    this.updateChartSeries();
  }

  chartError(reason?: string) {
    this.chartNotification.set(`No data found (${reason})`);
    this.chartRef.hideLoading();
    // hideNoData, because the string is not updated unless hidden.
    this.chartRef.hideNoData();
    this.chartRef.showNoData(this.chartNotification());
  }

  resetChartError() {
    this.chartNotification.set("Retrieving data...");
    this.chartRef.hideNoData();
    this.chartRef.showNoData(this.chartNotification());
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["subscription"] && this.subscription() !== undefined && this.subscription() !== null) {
      if (this.isWireless()) {
        this.defaultTimeWindow = 7 * 24 * 3600 * 1000;
        this.firstDate = new Date(Date.now() + -this.defaultTimeWindow);
      }

      // Maurits: commented out this code since it did set the portFilter which caused a wrong call to the health endpoint
      // TODO: @gravity please fix portFilter call to health endpoint.
      if (this.isLightPath()) {
        this.circuitFilter = this.circuits()[0];
        // this.portFilter = this.subscription._circuits[0].endpoints[0];
      }

      if (this.isWireless()) {
        this.getNewWirelessData();
      } else {
        this.getNewData();
      }
    }

    // if we reuse this component, remove all old data
    if (this.subscription() === null) {
      this.circuitFilter = "";
      this.portFilter = "";
      this.kpiFilter = "clients";
      this.clearAllData();
    }

    // only run when property "splineData" changed
    if (changes["splineData"]) {
      this.updateChartSeries();
    }
  }

  clearAllData() {
    if (this.chartRef) {
      while (this.chartRef.series.length > 0) {
        this.chartRef.series[0].remove(false);
      }
      this.chartRef.redraw(); // update the visual aspect
    }
  }

  getNewData() {
    if (this.chartRef !== undefined) {
      this.chartRef.showLoading(" ");
      this.resetChartError();
    }

    if (!this.subscription()) {
      return;
    }

    let subscriptionId = this.subscriptionId();
    let serviceInstanceId = "";
    let service = null;

    // if a linkmember is selected, use the selected InstanceId
    if (this.instanceIdFilter !== "") {
      subscriptionId = this.instanceIdFilter;
      // default value for instanceId is the subscriptionInstanceId of the linked service.
      if (this.vlans !== null) {
        service = this.services().find((e) => e.subscriptionId === this.instanceIdFilter && e.vlanrange === this.vlans);
      } else {
        service = this.services().find((e) => e.subscriptionId === this.instanceIdFilter);
      }

      if (service && service.product.productType !== "Port") {
        serviceInstanceId = service.portSubscriptionInstanceId;
      }
    }

    if (this.portFilter) {
      if (this.portFilter.firewallSubscriptionId !== undefined) {
        if (service && service.product.productType === "FW") {
          subscriptionId = this.portFilter.firewallSubscriptionId;
          serviceInstanceId = this.portFilter.subscriptionInstanceId;
        } else {
          subscriptionId = this.portFilter.firewallSubscriptionId;
          serviceInstanceId = "";
        }
      } else {
        serviceInstanceId = this.portFilter.subscriptionInstanceId;
      }
    }

    // splineData;

    this.api
      .service_stats_aggregated(
        subscriptionId,
        serviceInstanceId,
        this.chartDataModus(),
        this.packetsSubFilterActive,
        this.firstDate,
        this.secondDate,
      )
      .then((chartData) => {
        if (chartData.error === true) {
          this.chartError("api error");
        } else {
          this.splineDataRaw = chartData;
          this.updateChartSeries();
        }
      })
      .catch((err) => {
        SurfLog.err(`Linechart: Failed to load aggregated stats for ${this.subscriptionId()}`);
        this.chartError("http failure");
      });

    this.portsFilterData = this.loadPortsFilterData();
  }

  getNewWirelessData() {
    if (this.chartRef !== undefined) {
      this.chartRef.showLoading(" ");
      this.resetChartError();
    }

    const subscription: WirelessSubscriptionDetails = this.subscription();
    if (!subscription) {
      return;
    }

    const subscriptionId = subscription.subscriptionId;
    const frequency = this.kpiFilter === "clients" ? "5" : "all";

    // if switch KPIs ensure that no other line is left in the graph
    for (let i = 1; this.chartRef && i < this.chartRef.series.length; i++) {
      this.chartRef.series[i].remove();
    }

    this.api
      .wireless_stats_aggregated(subscriptionId, this.kpiFilter, frequency, this.firstDate, this.secondDate)
      .then((chartData) => {
        if (chartData.error === true) {
          this.chartError("api error");
        } else {
          this.splineDataRaw = chartData;
          this.updateWirelessChartSeries();
        }
      })
      .catch((err) => {
        SurfLog.err(`Linechart: Failed to load aggregated stats for ${this.subscriptionId()}`);
        this.chartError("http failure");
      });

    this.portsFilterData = this.loadPortsFilterData();
  }

  onClickedOutside() {
    if (this.exportOverflowVisible) {
      this.exportOverflowVisible = false;
    }
  }

  updateChartSeries() {
    if (this.splineDataRaw === undefined || this.splineDataRaw === null) {
      return;
    }

    let splineDataRaw = this.splineDataRaw;
    if (!this.showBits()) {
      splineDataRaw = this.splineDataRaw[this.packetsSubFilterActive];
    }

    this.splineData = {
      in: splineDataRaw.in,
      out: splineDataRaw.out,
      spike_in: splineDataRaw.spike_in,
      spike_out: splineDataRaw.spike_out,
    };
    this.latestChartData = {
      in: splineDataRaw.in[splineDataRaw.in.length - 1][1],
      out: splineDataRaw.out[splineDataRaw.out.length - 1][1],
    };

    if (this.splineData.in.length === 0 && this.splineData.out.length === 0) {
      this.chartError("empty");
    }

    this.repopulateChart();
  }

  updateWirelessChartSeries() {
    if (this.splineDataRaw === undefined || this.splineDataRaw === null) {
      return;
    }

    this.splineData = { five: this.splineDataRaw["five"] };
    if (this.kpiFilter !== "clients" && "twofour" in this.splineDataRaw) {
      this.splineData["twofour"] = this.splineDataRaw["twofour"];
    }

    this.repopulateChart();
  }

  loadL2VpnPortsFilterData(): PortFilterItem[] {
    const portTitle = this.translate.instant("ServiceElement.Port");
    const firewallInterconnectTitle = this.translate.instant("ServiceElement.FirewallInterconnect");

    const returnValues: PortFilterItem[] = [];

    for (const firewallInterconnect of this.subscription().firewallInterconnects || []) {
      returnValues.push({
        name: firewallInterconnectTitle,
        value: firewallInterconnect,
      });
    }

    let portCounter = 1;
    for (const esis of this.subscription().esis) {
      for (const endpoint of esis?.endpoints) {
        returnValues.push({
          name: `${portTitle} ${portCounter} - ${endpoint.port.description}`,
          value: endpoint,
        });
        portCounter++;
      }
    }

    return returnValues;
  }

  loadL3VpnPortsFilterData(): PortFilterItem[] {
    const portTitle = this.translate.instant("ServiceElement.Port");
    const firewallInterconnectTitle = this.translate.instant("ServiceElement.FirewallInterconnect");

    const returnValues: PortFilterItem[] = [];

    for (const thisFirewallInterconnect of this.subscription().firewallInterconnects) {
      returnValues.push({
        name: firewallInterconnectTitle,
        value: thisFirewallInterconnect,
      });
    }

    let portCounter = 1;
    for (const endpoint of this.subscription().l3vpnsapss) {
      returnValues.push({
        name: `${portTitle} ${portCounter} - ${endpoint.sap.port.description}`,
        value: endpoint.sap,
      });
      portCounter++;
    }

    return returnValues;
  }

  loadFirewallPortsFilterData(): PortFilterItem[] {
    const internetUplinkTitle = this.translate.instant("ServiceElement.SURFinternetUplink");

    const returnValues: PortFilterItem[] = [];

    returnValues.push({
      name: internetUplinkTitle,
      value: this.subscription().ipGateway,
    });

    for (const endpoint of this.subscription().l2Endpoints) {
      returnValues.push({
        name: endpoint.description,
        value: endpoint,
      });
    }

    for (const endpoint of this.subscription().l3Endpoints) {
      returnValues.push({
        name: endpoint.description,
        value: endpoint,
      });
    }

    return returnValues;
  }

  loadPortsFilterData(): PortFilterItem[] {
    switch (this.product().type) {
      case "L2VPN":
        return this.loadL2VpnPortsFilterData();
      case "L3VPN":
        return this.loadL3VpnPortsFilterData();
      case "FW":
        return this.loadFirewallPortsFilterData();
      default:
        return [];
    }
  }

  private repopulateChart() {
    const lineColors = {
      0: "#177abf",
      1: "#e7303a",
      2: "#ee7628",
      3: "#2ca055",
    };

    this.chartRef.showLoading(" ");

    let indexNow = 0;
    let noDataFound = true;

    for (const serieName in this.splineData) {
      if (this.chartRef.series[indexNow] === undefined) {
        this.chartRef.addSeries(
          {
            name: serieName,
            data: this.splineData[serieName],
            type: this.chartInfo.type,
            color: lineColors[indexNow],
          },
          false,
          false,
        );
      } else {
        this.chartRef.series[indexNow].update({ name: serieName, type: this.chartInfo.type }, false);
        this.chartRef.series[indexNow].setData(this.splineData[serieName], false, false, false);
      }

      if (this.splineData[serieName].length > 0) {
        noDataFound = false;
      }
      indexNow++;
    }

    if (noDataFound) {
      this.chartError("empty response");
    }

    this.chartRef.redraw(true);
    this.chartRef.hideLoading();
    this.chartRef.zoom(); // force zoom out
  }
}
